import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { staticContent } from '@src/routes'
import { useUserSession } from '@utils/useUserSession'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
  Button,
  Typography,
  Theme,
  Grid,
  Box,
  useMediaQuery,
} from '@material-ui/core'
import messages from './messages'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import MuiLink from '@material-ui/core/Link'
import { Link } from '@src/components/Link'

export const useStyles = makeStyles((theme: Theme) => {
  return {
    bodyText: {
      fontSize: theme.typography.pxToRem(14),
    },
    smartLabel: {
      fontSize: theme.typography.pxToRem(14),
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
    },
    tryScanNowText: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(2),
    },
    downloadImage: {
      padding: `${theme.spacing(4)}px 0`,
      [theme.breakpoints.up('md')]: {
        padding: 0,
      },
    },
    modalBody: {
      overflowY: 'auto',
      padding: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        padding: '15px 32px',
      },
    },
    okButton: {
      width: '100%',
      textAlign: 'end',
    },
    image: {
      width: '100%',
      height: 'auto',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
        height: 'auto',
      },
    },
  }
})
interface ScanNowUsageModalProps {
  open: boolean
  setModalOpen: (open: boolean) => void
}

const ScanNowUsageModal: React.FC<ScanNowUsageModalProps> = ({
  open,
  setModalOpen,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const {
    userSession: { language, country },
  } = useUserSession()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  return (
    <>
      {open ? (
        <ResponsiveModal
          open
          size={ModalSizes.Large}
          onClose={() => setModalOpen(false)}
          aria-labelledby="how-to-use-scan-now"
          renderTitle={() => (
            <Typography component="h2" variant="h2">
              <FormattedMessage {...messages.HOW_TO_USE_SCAN_NOW} />
            </Typography>
          )}
          raisedCloseButton
        >
          <Grid container className={classes.modalBody}>
            <Grid item xs={12}>
              <Typography component="h3" variant="h3">
                <FormattedMessage {...messages.WHAT_IS_SCAN_NOW} />
              </Typography>
              <Box mt={2}>
                <Typography className={classes.bodyText}>
                  <FormattedMessage
                    {...messages.SCAN_NOW_EXPLANATION}
                    values={{
                      SmartLabel: (
                        <Link
                          {...staticContent.index({
                            path: '/support/smart-label',
                            language,
                            country,
                          })}
                          passHref
                        >
                          <MuiLink target="_blank">
                            <FormattedMessage
                              {...messages.SCAN_NOW_SMART_LABEL}
                            />
                          </MuiLink>
                        </Link>
                      ),
                    }}
                  />
                </Typography>
              </Box>
            </Grid>
            <Box mt={4}>
              <Grid container>
                <Grid item md={5} lg={5} sm={12} xs={12}>
                  <img
                    alt="Barcode Reader"
                    src="/static/barcode_reader.jpg"
                    className={classes.image}
                  />
                </Grid>
                <Grid item md={7} lg={7} sm={12} xs={12}>
                  <Typography className={classes.bodyText}>
                    <FormattedMessage
                      {...messages.SCAN_NOW_INSTRUCTIONS}
                      values={{
                        barcodes: (
                          <strong>GS1 two-dimensional (2D) barcodes</strong>
                        ),
                      }}
                    />
                  </Typography>
                  <Box id="smart-label">
                    <Typography className={classes.smartLabel}>
                      <FormattedMessage {...messages.SAMPLE_SMART_LABEL} />:
                    </Typography>
                    <img alt="Scan Now Barcode" src="/static/barcode.png" />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Grid container>
              <Typography variant="h3" className={classes.tryScanNowText}>
                <FormattedMessage {...messages.TRY_THE_SCAN_NOW_MOBILE_APP} />
              </Typography>
              <Grid container>
                <Grid item md={6} lg={6} sm={12} xs={12}>
                  <Typography className={classes.bodyText}>
                    <FormattedMessage {...messages.SCAN_NOW_MOBILE_APP_INFO} />
                    <FormattedMessage
                      {...messages.SCAN_NOW_MOBILE_AVAILABILITY}
                      values={{
                        iOS: (
                          <MuiLink
                            href="https://apps.apple.com/us/app/milliporesigma-scan-now/id1504834619"
                            target="_blank"
                          >
                            {formatMessage(messages.IOS)}
                          </MuiLink>
                        ),
                        AndroidBeta: (
                          <MuiLink
                            href="https://drive.google.com/open?id=1ZL31wIsWUHsGXo2jJB2OnWWIYpfTGmz-"
                            target="_blank"
                          >
                            {formatMessage(messages.ANDROID_BETA)}
                          </MuiLink>
                        ),
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid
                  item
                  md={6}
                  lg={6}
                  sm={12}
                  xs={12}
                  className={classes.downloadImage}
                >
                  <img alt="Apple App Store" src="/static/apple_appstore.png" />
                </Grid>
              </Grid>
            </Grid>
            <Box mt={4} mb={4} className={classes.okButton}>
              <Button
                variant="contained"
                color="primary"
                fullWidth={!isDesktop}
                onClick={() => setModalOpen(false)}
              >
                <FormattedMessage {...messages.OK} />
              </Button>
            </Box>
          </Grid>
        </ResponsiveModal>
      ) : null}
    </>
  )
}

export default ScanNowUsageModal
